if (!window.ProductSizeSelection) {
  window.ProductSizeSelection = class ProductSizeSelection {
    static getVariantSelects(sectionId) {
      return document.getElementById(`variant-selects-${sectionId}`);
    }

    static hasSizeOption(sectionId) {
      return !!this.getVariantSelects(sectionId)?.querySelector('.product-form__input--size');
    }

    static isSizeSelected(sectionId) {
      const variantSelects = this.getVariantSelects(sectionId);
      if (!variantSelects) return true;

      const sizeFieldset = variantSelects.querySelector('.product-form__input--size');
      if (!sizeFieldset) return true;

      if (sizeFieldset.querySelector('input[type="radio"]:checked')) return true;

      const select = sizeFieldset.querySelector('select');
      if (select) {
        const selectedOption = select.selectedOptions[0];
        return !!(selectedOption?.value && !selectedOption.disabled);
      }

      return false;
    }

    static getSizeElement(sectionId) {
      return (
        document.getElementById(`SizeSelector-${sectionId}`) ||
        this.getVariantSelects(sectionId)?.querySelector('.product-form__input--size')
      );
    }

    static scrollToSize(sectionId) {
      this.getSizeElement(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    static promptSizeSelection(sectionId) {
      const target = this.getSizeElement(sectionId);
      if (!target) return;

      const rect = target.getBoundingClientRect();
      const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
      if (!inView) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      target.classList.remove('is-shaking');
      void target.offsetWidth;
      target.classList.add('is-shaking');
      target.addEventListener(
        'animationend',
        () => {
          target.classList.remove('is-shaking');
        },
        { once: true }
      );
    }

    static getVariantDisplayLabel(sectionId) {
      const variantSelects = this.getVariantSelects(sectionId);
      if (!variantSelects) return '';

      const parts = [];

      variantSelects.querySelectorAll('.product-form__input:not(.product-form__input--size)').forEach((fieldset) => {
        const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
        if (checkedRadio?.value) {
          parts.push(checkedRadio.value);
          return;
        }

        const select = fieldset.querySelector('select');
        if (select?.value) parts.push(select.value);
      });

      const sizeFieldset = variantSelects.querySelector('.product-form__input--size');
      if (sizeFieldset) {
        const checkedSize = sizeFieldset.querySelector('input[type="radio"]:checked');
        if (checkedSize?.value) {
          parts.push(checkedSize.value);
        } else {
          const sizeSelect = sizeFieldset.querySelector('select');
          if (sizeSelect?.value) parts.push(sizeSelect.value);
        }
      }

      return parts.map((value) => value.toUpperCase()).join(' / ');
    }

    static getVariantInput(sectionId) {
      return document.querySelector(`#product-form-${sectionId} input[name="id"]`);
    }

    static restoreSizeSelection(sectionId, sizeValue) {
      if (!sizeValue) return false;

      const variantSelects = this.getVariantSelects(sectionId);
      const sizeFieldset = variantSelects?.querySelector('.product-form__input--size');
      if (!sizeFieldset) return false;

      const radio = sizeFieldset.querySelector(
        `input[type="radio"][value="${CSS.escape(sizeValue)}"]`
      );
      if (radio && !radio.disabled) {
        radio.checked = true;
        return true;
      }

      const select = sizeFieldset.querySelector('select');
      if (select) {
        select.value = sizeValue;
        return true;
      }

      return false;
    }

    static clearSizeSelection(sectionId) {
      const variantSelects = this.getVariantSelects(sectionId);
      const sizeFieldset = variantSelects?.querySelector('.product-form__input--size');
      if (!sizeFieldset) return;

      sizeFieldset.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        input.checked = false;
      });

      const select = sizeFieldset.querySelector('select');
      if (select) {
        select.selectedIndex = -1;
      }

      const selectedValue = sizeFieldset.querySelector('[data-selected-value]');
      if (selectedValue) selectedValue.textContent = '';
    }

    static updateSubmitButtonState(sectionId) {
      if (!this.hasSizeOption(sectionId)) return false;

      const needsSize = !this.isSizeSelected(sectionId);
      const mainButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
      const label = mainButton?.querySelector('.product-form__submit-label');
      const variantInput = this.getVariantInput(sectionId);

      if (needsSize) {
        mainButton?.removeAttribute('disabled');
        if (variantInput) {
          variantInput.value = '';
          variantInput.setAttribute('disabled', 'disabled');
        }
      } else {
        if (variantInput) {
          variantInput.removeAttribute('disabled');
        }
        // The product section response already supplies the correct label
        // (ADD TO CART, PRE-ORDER, or SOLD OUT) for the selected variant.
      }

      document
        .querySelector(`floating-add-to-cart[data-section-id="${sectionId}"]`)
        ?.updateSizeState?.();

      return needsSize;
    }

    static bindProductInfo(productInfo) {
      if (productInfo.dataset.sizeSelectionBound === 'true') return;
      productInfo.dataset.sizeSelectionBound = 'true';

      productInfo.addEventListener('change', (event) => {
        const variantSelects = productInfo.querySelector('variant-selects');
        if (!variantSelects?.contains(event.target)) return;

        if (event.target.closest('.product-form__input--size')) {
          productInfo.sizeWasSelected = true;
          productInfo.lastSelectedSizeValue = event.target.value;
        }

        this.updateSubmitButtonState(productInfo.sectionId);
      });
    }

    static initProductInfo(productInfo) {
      const sectionId = productInfo.sectionId;
      if (!this.hasSizeOption(sectionId)) return;

      const urlVariant = new URLSearchParams(window.location.search).get('variant');
      productInfo.sizeWasSelected = !!urlVariant;

      if (!productInfo.sizeWasSelected) {
        this.clearSizeSelection(sectionId);
      }

      this.bindProductInfo(productInfo);
      this.updateSubmitButtonState(sectionId);
    }

    static preserveSizeStateAfterUpdate(productInfo) {
      const sectionId = productInfo.sectionId;
      if (!this.hasSizeOption(sectionId)) return;

      if (productInfo.sizeWasSelected) {
        if (!this.isSizeSelected(sectionId) && productInfo.lastSelectedSizeValue) {
          this.restoreSizeSelection(sectionId, productInfo.lastSelectedSizeValue);
        }

        const variant = productInfo.getVariantFromSelectedOptions();
        if (variant?.id) {
          productInfo.updateVariantInputs(variant.id);
        }

        this.updateSubmitButtonState(sectionId);
        return;
      }

      this.clearSizeSelection(sectionId);
      this.updateSubmitButtonState(sectionId);
    }
  };
}
